import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { getClientId } from "@/lib/clientId";

/**
 * Records every page view (initial load + each SPA navigation) to the backend
 * visit log so the admin Live Pulse shows each visitor's full journey.
 *
 * Design (robust against React StrictMode double-mounts & remounts):
 * - Dedup state lives at MODULE scope (`lastTracked`), not in a ref/state, so it
 *   survives StrictMode's simulated unmount/remount and any re-render. This
 *   guarantees exactly ONE POST per distinct path per page load.
 * - `lastTracked === null` means this is the first hit of a fresh page load, so
 *   we forward `document.referrer` for traffic-source classification. Subsequent
 *   in-app navigations are same-origin and send an empty referrer.
 * - The backend decides whether a hit starts a new session (insert + increment)
 *   or continues an existing one (append to the journey). The frontend no longer
 *   guesses "first vs not" — that flakey sessionStorage flag caused the initial
 *   hit to be mislabeled and journeys to look truncated.
 * - Fires only in the browser; silent on error so tracking never blocks routing.
 */
let lastTracked = null;

export default function SiteVisitTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Internal author navigation shouldn't pollute the visitor log.
    if (location.pathname.startsWith("/admin")) return;

    const path = location.pathname + location.search;
    if (lastTracked === path) return; // dedupe repeat fires / StrictMode

    const isFirstOfPageLoad = lastTracked === null;
    lastTracked = path;

    api
      .post("/stats/visit", {
        client_id: getClientId(),
        path,
        // Only meaningful on the first hit of a fresh page load; in-app navs are
        // same-origin and would otherwise clobber the true external source.
        referrer: isFirstOfPageLoad ? (document.referrer || "") : "",
      })
      .catch((err) => {
        // Swallow — tracking must never break the app.
        console.error("Visit tracker failed:", err);
      });
  }, [location.pathname, location.search]);

  return null;
}
